/**
 * InputField.js - Form Input Components Library
 * 
 * This file contains all the reusable form input components used throughout the application.
 * Each component handles different types of form inputs with consistent styling and validation.
 * 
 * Components included:
 * - TextInput: Basic text input field
 * - PasswordInput: Password input with masking
 * - IntegerInput: Numeric input for integers
 * - SliderInput: Range slider input
 * - BooleanInput: Toggle switch for boolean values
 * - TextAreaInput: Multi-line text input
 * - DateInput: Date picker input
 * - DropDownInput: Custom dropdown with search and multi-select
 * - RelationDropDownInput: Dropdown for related entities with create options
 * - FileInput: File upload input
 * - CustomFrequencyInput: Custom frequency input for metrics
 * - MultiSelectInput: Multi-select dropdown
 * - TreeInput: Hierarchical tree structure input
 * - MetricSelectorInput: Dynamic metric selector with modal
 * - FormulaInput: Mathematical formula input with validation
 * 
 * @author System
 * @version 1.0.0
 */

import { 
  Button, 
  DatePicker, 
  Dropdown, 
  DropdownItem, 
  DropdownMenu, 
  DropdownTrigger, 
  Input, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  Select, 
  SelectItem, 
  Slider, 
  Switch, 
  Textarea, 
  Chip,
  Spinner
} from "@nextui-org/react";

import { ChangeDateDDToYYYY, DateFormatorDD } from "../../../utils/helper";
import { FaChevronDown } from "react-icons/fa";
import { CreateNewEntryButton } from "../../../utils/helper";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { parseDate } from "@internationalized/date";
import { uploadFile, getDataWithFilterUrl, getAlldata } from "../../../controllers/APIController";

import FieldInputTooltip from "../../../components/utils/FieldInputTooltip";
import { FieldList } from "../../../utils/constant";
import { createPortal } from "react-dom";
import { StandardSecondaryButton, StandardPrimaryButton, StandardCancelButton, StandardDeleteButton, StandardAddButton, StandardCopyButton } from "../../../components/StandardButtons";

/**
 * TextInput Component
 * 
 * Renders a basic text input field with validation and tooltip support.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {boolean} data.isDisable - Whether the field is disabled
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.placeholder - Placeholder text
 * @param {string} data.startContent - Content to display before the input
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Text input component
 */
export const TextInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4",
}) => {
  // Handle input value changes
  const inputHandler = (event) => {
    onChangeHandler(data?.title, event.target.value, data?.type);
  };

  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-6" : cssStyle;

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Input
          type="text"
          isReadOnly={data?.isDisable === true ? true : false}
          isRequired={data?.required || false}
          placeholder={data?.placeholder || " "}
          variant="faded"
          labelPlacement="outside"
          value={value}
          onChange={inputHandler}
          startContent={data?.startContent}
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`
          }}
        />

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * PasswordInput Component
 * 
 * Renders a password input field with masking and validation.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {number} data.char_limit - Maximum character limit
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.placeholder - Placeholder text
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Password input component
 */
export const PasswordInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-16",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : "mb-12";

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Input
          type="password"
          isRequired={data?.required || false}
          maxLength={data?.char_limit}
          placeholder={data?.placeholder || " "}
          variant="faded"
          labelPlacement="outside"
          value={value}
          onChange={(event) => {
            onChangeHandler(data?.title, event.target.value, data?.type);
          }}
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`
          }}
        />

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * IntegerInput Component
 * 
 * Renders a numeric input field for integer values with min/max validation.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {number} data.min_val - Minimum allowed value
 * @param {number} data.max_val - Maximum allowed value
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Integer input component
 */
export const IntegerInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-6" : cssStyle;

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Input
          type="number"
          isRequired={data?.required || false}
          min={data?.min_val}
          max={data?.max_val}
          placeholder="0"
          variant="faded"
          labelPlacement="outside"
          value={value && value}
          onChange={(event) => {
            // Convert string to number for numeric input
            onChangeHandler(data?.title, +event.target.value, data?.type);
          }}
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`
          }}
        />

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * SliderInput Component
 * 
 * Renders a range slider input for numeric values within a specified range.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {number} data.max_val - Maximum slider value
 * @param {number} data.min_val - Minimum slider value
 * @param {number} data.step_val - Step increment for slider
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Slider input component
 */
export const SliderInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-16",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : "mb-16";

  return (
    <div className={`flex gap-2 ${cssStyle}`}>
      <Slider
        isRequired={data?.required || false}
        label={data?.title || "Title"}
        maxValue={data?.max_val || 100}
        minValue={data?.min_val || 0}
        step={data?.step_val || 1}
        defaultValue={value || 0}
        value={value || 0}
        onChange={(value) => {
          onChangeHandler(data?.title, value, data?.type);
        }}
        isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
        errorMessage={validationErrors && validationErrors[data?.title]}
      />
    </div>
  );
};

/**
 * BooleanInput Component
 * 
 * Renders a toggle switch for boolean values (true/false).
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value ("true" or "false")
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Boolean input component
 */
export const BooleanInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-6" : cssStyle;

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="flex items-center justify-between p-3 bg-white border border-neutral-200 rounded-lg hover:border-neutral-300 transition-all duration-200">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-neutral-700">
              {data?.title}
            </span>
            {data?.required && <span className="text-xs text-red-500">Required</span>}
          </div>
          <Switch
            defaultSelected={value === "true"}
            isSelected={value === "true" ? true : false}
            color="primary"
            size="md"
            onChange={(event) =>
              onChangeHandler(
                data?.title,
                event.target.checked.toString(),
                data?.type
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * TextAreaInput Component
 * 
 * Renders a multi-line text area input for longer text content.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {number} data.char_limit - Maximum character limit
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.placeholder - Placeholder text
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Text area input component
 */
export const TextAreaInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-6" : cssStyle;

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Textarea
          key={data?.title}
          isRequired={data?.required || false}
          value={value}
          // maxLength={data?.char_limit}
          placeholder={data?.placeholder || " "}
          variant="faded"
          labelPlacement="outside"
          onChange={(event) => {
            onChangeHandler(data?.title, event.target.value, data?.type);
          }}
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`
          }}
        />

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * DateInput Component
 * 
 * Renders a date picker input with smart date parsing and positioning logic.
 * Handles different date formats and automatically positions the picker to avoid viewport issues.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value (supports multiple date formats)
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Date input component
 */
export const DateInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-4",
}) => {
  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-6" : cssStyle;

  // State for positioning the date picker to avoid viewport issues
  const [position, setPosition] = useState({});
  const datePickerRef = useRef();

  // Handle scroll events to reposition the date picker if needed
  useEffect(() => {
    const handleScroll = () => {
      const rect = datePickerRef.current.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        setPosition({ top: "auto", bottom: 0 });
      } else {
        setPosition({});
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div ref={datePickerRef} style={position} className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <DatePicker
          labelPlacement="outside"
          isRequired={data?.required || false}
          aria-label={data?.title || "Date picker"}
          onChange={(value) => {
            onChangeHandler(data?.title, DateFormatorDD(value), data?.type);
          }}
          value={
            value && value !== "" && value !== "null" && value !== undefined
              ? (() => {
                  try {
                    // Check if value is already in YYYY-MM-DD format
                    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                      return parseDate(value);
                    }
                    // Check if value is in DD-MM-YYYY format
                    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
                      return parseDate(ChangeDateDDToYYYY(value));
                    }
                    // If it's a valid date string, try to parse it directly
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      return parseDate(`${year}-${month}-${day}`);
                    }
                    return null;
                  } catch (error) {
                    console.error('Error parsing date:', error);
                    return null;
                  }
                })()
              : null
          }
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`
          }}
        />
      </div>

      {validationErrors && validationErrors[data?.title] && data?.required && (
        <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
      )}
        </div>
      );
    };

/**
 * DropDownInput Component
 * 
 * Renders a dropdown input with search functionality and multi-select support.
 * Now uses a right-side drawer instead of a traditional dropdown for consistency.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {boolean} data.required - Whether the field is required
 * @param {boolean} data.ismultiselect - Whether multiple selections are allowed
 * @param {Array} data.value - Array of available options
 * @param {Array} data.values - Alternative array of available options
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value (comma-separated for multi-select)
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} buttonCSS - CSS class for button styling
 * @param {string} cssStyle - CSS class for overall styling
 * @returns {JSX.Element} Dropdown input component with drawer
 */
export const DropDownInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  buttonCSS = "mb-16", 
  cssStyle = "mb-12",
}) => {
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : buttonCSS;
  const valueList = data?.value || data?.values;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState(
    value?.trim() !== "" ? new Set(value?.split(", ")) : new Set()
  );

  // Filter items based on search query
  const filteredItems = valueList?.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Handle item selection
  const handleItemSelect = (itemName) => {
    let newSelectedItems;

    if (data?.ismultiselect) {
      newSelectedItems = new Set(selectedItems);
      if (newSelectedItems.has(itemName)) {
        newSelectedItems.delete(itemName);
      } else {
        newSelectedItems.add(itemName);
      }
    } else {
      newSelectedItems = new Set([itemName]);
      setIsDrawerOpen(false);
    }

    setSelectedItems(newSelectedItems);
    const selectedValues = Array.from(newSelectedItems).join(", ");
    onChangeHandler(data?.title, selectedValues, data?.type);
  };

  // Update selected items when value prop changes
  useEffect(() => {
    setSelectedItems(
      value?.trim() !== "" ? new Set(value?.split(", ")) : new Set()
    );
  }, [value]);

  const displayValue = Array.from(selectedItems).join(", ");

  return (
    <div className={`flex flex-col gap-2 w-full ${cssStyle}`}>
      <div className="flex flex-col gap-2 w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Button
          variant="faded"
          className={`w-full justify-between text-sm font-medium bg-white border border-neutral-200 hover:border-neutral-300 focus:border-emerald-500 transition-all duration-200 ${
            validationErrors && validationErrors[data?.title] && data?.required
              ? "border-red-300"
              : ""
          }`}
          onPress={() => setIsDrawerOpen(true)}
        >
          <div className="w-full flex justify-between">
            <div className="flex justify-start w-full items-start truncate">
              {displayValue || "Select an option"}
            </div>
            <div className="flex justify-end w-full items-end">
              <FaChevronDown />
            </div>
          </div>
        </Button>

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1 text-xs">{validationErrors[data?.title]}</p>
        )}

        {/* Debug info */}
        <div className="text-xs text-gray-500 mt-2">
          Items available: {valueList?.length || 0}
          {displayValue && ` | Selected: ${displayValue}`}
          {value && ` | Form value: ${value}`}
        </div>

        {/* Full-page modal for options */}
        {isDrawerOpen && createPortal(
          <div
            className="fixed inset-0"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              pointerEvents: 'auto'
            }}
          >
            {/* Backdrop */}
            <div
              className={`bg-black bg-opacity-50 backdrop-blur-sm transition-all duration-300 ${
                isDrawerOpen ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={() => setIsDrawerOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999
              }}
            />

            {/* Centered modal instead of right-side drawer */}
            <div
              className={`w-[600px] max-h-[80vh] bg-white shadow-2xl transition-all duration-300 ease-in-out rounded-xl border border-gray-200 flex flex-col ${
                isDrawerOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                maxHeight: '80vh',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                zIndex: 10000,
                maxWidth: '90vw',
                maxHeight: '90vh',
                isolation: 'isolate',
                contain: 'layout style paint',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                boxSizing: 'border-box'
              }}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select {data?.title}
                </h3>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Search bar */}
              <div
                className="p-6 border-b border-gray-200"
                style={{
                  all: 'unset',
                  padding: '1.5rem',
                  borderBottom: '1px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              >
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search options..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-base"
                    autoFocus
                    style={{
                      all: 'unset',
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      outline: 'none',
                      fontSize: '1rem',
                      lineHeight: '1.5',
                      backgroundColor: 'white',
                      color: '#111827',
                      boxSizing: 'border-box'
                    }}
                  />
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchQuery && (
                  <div className="mt-3 text-sm text-gray-500">
                    {filteredItems.length} of {valueList?.length || 0} options match your search
                  </div>
                )}
              </div>

              {/* Modal content */}
              <div
                className="flex-1 overflow-y-auto"
                style={{
                  all: 'unset',
                  flex: '1 1 0%',
                  overflowY: 'auto',
                  boxSizing: 'border-box'
                }}
              >
                {filteredItems.length > 0 ? (
                  <div className="p-6 space-y-3">
                    {filteredItems.map((item) => {
                      const isSelected = selectedItems.has(item.name);

                      return (
                        <div
                          key={item.name}
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                              : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-25 hover:shadow-sm'
                          }`}
                          onClick={() => handleItemSelect(item.name)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{item.name}</span>
                            {isSelected && (
                              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-lg font-medium">No results found</p>
                    <p className="text-sm">Try adjusting your search terms.</p>
                  </div>
                )}
              </div>

              {/* Multi-select actions */}
              {data?.ismultiselect && selectedItems.size > 0 && (
                <div
                  className="p-6 border-t border-gray-200 bg-gray-50"
                  style={{
                    all: 'unset',
                    padding: '1.5rem',
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex gap-2">
                      <StandardDeleteButton
                        size="sm"
                        onClick={() => {
                          setSelectedItems(new Set());
                          onChangeHandler(data?.title, "", data?.type);
                        }}
                      >
                        Clear
                      </StandardDeleteButton>
                      <StandardPrimaryButton
                        size="sm"
                        onClick={() => setIsDrawerOpen(false)}
                      >
                        Done
                      </StandardPrimaryButton>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal footer for single select */}
              {!data?.ismultiselect && (
                <div
                  className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl"
                  style={{
                    all: 'unset',
                    padding: '1.5rem',
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    borderBottomLeftRadius: '0.75rem',
                    borderBottomRightRadius: '0.75rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {filteredItems.length} option{filteredItems.length !== 1 ? 's' : ''} available
                    </span>
                    <button
                      onClick={() => setIsDrawerOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                      style={{
                        all: 'unset',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

/**
 * RelationDropDownInput Component
 * 
 * Renders a relation dropdown input with create options and tooltip support.
 * Now uses a right-side drawer instead of a traditional dropdown.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {string} data.relatedto - Comma-separated list of related metric IDs
 * @param {string} data.metricname - Metric name for create buttons
 * @param {Array} data.values - Array of available values for the relation
 * @param {string} data.framework - Framework context for tooltips
 * @param {string} value - Current field value
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {Function} onClickHandler - Function to handle create button clicks
 * @param {string} buttonCSS - CSS class for button styling
 * @param {string} cssStyle - CSS class for overall styling
 * @returns {JSX.Element} Relation dropdown input component with drawer
 */
export const RelationDropDownInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  onClickHandler, 
  buttonCSS = "mb-16", 
  cssStyle = "mb-12",
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedItems, setDisplayedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; // Show 50 items at a time

  // Component data received
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : buttonCSS;

  // Add null checks for relatedto
  const relatedto = data?.relatedto || data?.relatedTo || "";
  const metricIDList = relatedto ? relatedto.split(",") : []; // Metric ID list processed

  const createEntryButtonList = React.useMemo(() => {
    return metricIDList.map((metricId) => {
      return {
        id: `create-${metricId}`,
        name: (
          <CreateNewEntryButton
            name={metricId}
            metricName={data?.metricname}
            onClickHandler={onClickHandler}
          />
        ),
      };
    });
  }, [metricIDList, data?.metricname, onClickHandler]); // Create entry button list generated

  const newRawValue = React.useMemo(() => {
    let rawValue = data?.values === undefined
      ? createEntryButtonList
      : [...createEntryButtonList, ...data?.values];

    // Filter out null or invalid values
    return rawValue.filter(item => {
      if (!item) return false;
      if (typeof item.name === 'string') {
        return item.name && item.name.trim() !== '' && item.name !== 'null' && item.name !== 'undefined';
      }
      return true; // Keep React components
    });
  }, [createEntryButtonList, data?.values]); // Final values prepared for drawer

  // Values structure processed

  // Find the selected item
  const selectedItem = newRawValue.find(item => {
    if (typeof item.name === 'string') {
      return item.name === value;
    }
    return false;
  }); // Selected item and current value processed

  const handleOptionSelect = (item) => {
    if (typeof item.name === 'string') {
      // Calling onChangeHandler
      onChangeHandler(data?.title, item.name, data?.type);
      // Close drawer after selection
      setIsDrawerOpen(false);
    } else {
      // Handle create new entry button clicks
    }
  };

  // Filter and paginate items based on search query
  const filteredItems = React.useMemo(() => {
    return newRawValue.filter(item => {
      if (typeof item.name === 'string') {
        return item.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true; // Always show create buttons
    });
  }, [newRawValue, searchQuery]);

  // Check if we have many options and show appropriate message
  const hasManyOptions = newRawValue.length > 50;
  const optionsCount = newRawValue.length;

  const totalPages = React.useMemo(() => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  }, [filteredItems.length, itemsPerPage]);

  const startIndex = React.useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const endIndex = React.useMemo(() => {
    return startIndex + itemsPerPage;
  }, [startIndex, itemsPerPage]);

  const currentItems = React.useMemo(() => {
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, startIndex, endIndex]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Update displayed items when data changes - use a more stable dependency
  useEffect(() => {
    if (filteredItems.length > 0) {
      setDisplayedItems(currentItems);
    }
  }, [filteredItems.length, currentPage, searchQuery]);

  return (
    <div className={`flex flex-col gap-2 w-full ${cssStyle}`}>
      <div className="flex flex-col gap-2 w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Button
          variant="faded"
          className={`w-full justify-between text-sm font-medium bg-white border border-neutral-200 hover:border-neutral-300 focus:border-emerald-500 transition-all duration-200 ${
            validationErrors && validationErrors[data?.title] && data?.required
              ? "border-red-300"
              : ""
          }`}
          onPress={() => setIsDrawerOpen(true)}
        >
          <div className="w-full flex justify-between">
            <div className="flex justify-start w-full items-start truncate">
              {value || "Select an option"}
            </div>
            <div className="flex justify-end w-full items-end">
              <FaChevronDown />
            </div>
          </div>
        </Button>

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1 text-xs">{validationErrors[data?.title]}</p>
        )}

        {/* Debug info */}
        <div className="text-xs text-gray-500 mt-2">
          Items available: {newRawValue?.length || 0}
          {selectedItem && ` | Selected: ${selectedItem.name}`}
          {value && ` | Form value: ${value}`}
        </div>

        {/* Full-page modal for options */}
        {isDrawerOpen && createPortal(
          <div
            className="fixed inset-0"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              pointerEvents: 'auto'
            }}
          >
            {/* Backdrop */}
            <div
              className={`bg-black bg-opacity-50 backdrop-blur-sm transition-all duration-300 ${
                isDrawerOpen ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={() => setIsDrawerOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999
              }}
            />

            {/* Centered modal instead of right-side drawer */}
            <div
              className={`w-[600px] max-h-[80vh] bg-white shadow-2xl transition-all duration-300 ease-in-out rounded-xl border border-gray-200 flex flex-col ${
                isDrawerOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                maxHeight: '80vh',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                zIndex: 10000,
                maxWidth: '90vw',
                maxHeight: '90vh',
                isolation: 'isolate',
                contain: 'layout style paint',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                boxSizing: 'border-box'
              }}
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select {data?.title}
                </h3>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Options count and help message */}
              {hasManyOptions && (
                <div className="px-6 pt-6 pb-2 border-b border-gray-200">
                  <div className="text-sm text-gray-600 bg-emerald-50 p-3 rounded-lg">
                    <p className="font-medium mb-1">Many options available ({optionsCount} total)</p>
                    <p>Use the search bar below to quickly find what you need. Only showing first 100 options.</p>
                  </div>
                </div>
              )}

              {/* Search bar */}
              <div
                className={`border-b border-gray-200 ${
                  hasManyOptions ? 'px-6 py-4' : 'p-6'
                }`}
                style={{
                  all: 'unset',
                  padding: hasManyOptions ? '1rem 1.5rem' : '1.5rem',
                  borderBottom: '1px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              >
                <div className="relative">
                  <input
                    type="text"
                    placeholder={`Search ${data?.title || 'options'}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-base"
                    autoFocus
                    style={{
                      all: 'unset',
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      outline: 'none',
                      fontSize: '1rem',
                      lineHeight: '1.5',
                      backgroundColor: 'white',
                      color: '#111827',
                      boxSizing: 'border-box'
                    }}
                  />
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchQuery && (
                  <div className="mt-3 text-sm text-gray-500">
                    {filteredItems.length} of {newRawValue.length} options match your search
                    {hasManyOptions && (
                      <span className="ml-2 text-emerald-600">
                        (showing first 100 of {optionsCount} total)
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Modal content */}
              <div
                className="flex-1 overflow-y-auto"
                style={{
                  all: 'unset',
                  flex: '1 1 0%',
                  overflowY: 'auto',
                  boxSizing: 'border-box'
                }}
              >
                {filteredItems.length > 0 ? (
                  <div className="p-6 space-y-3">
                    {currentItems.map((item, index) => {
                      const itemKey = item.id || `item-${startIndex + index}`;
                      const isSelected = typeof item.name === 'string' ? item.name === value : false;

                      return (
                        <div
                          key={itemKey}
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                              : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-25 hover:shadow-sm'
                          }`}
                          onClick={() => handleOptionSelect(item)}
                        >
                          {typeof item.name === 'string' ? (
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{item.name}</span>
                              {isSelected && (
                                <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              {item.name}
                              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">Create new</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-lg font-medium">No options found</p>
                    <p className="text-sm">
                      {searchQuery ? `No items match "${searchQuery}"` : 'There are no items to select from.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div
                  className="p-6 border-t border-gray-200 bg-gray-50"
                  style={{
                    all: 'unset',
                    padding: '1.5rem',
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    boxSizing: 'border-box'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        Page {currentPage} of {totalPages}
                      </span>
                      <span className="text-sm text-gray-400">
                        ({startIndex + 1}-{Math.min(endIndex, filteredItems.length)} of {filteredItems.length})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          all: 'unset',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          all: 'unset',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          boxSizing: 'border-box',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal footer */}
              <div
                className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl"
                style={{
                  all: 'unset',
                  padding: '1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderBottomLeftRadius: '0.75rem',
                  borderBottomRightRadius: '0.75rem',
                  boxSizing: 'border-box'
                }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {filteredItems.length} option{filteredItems.length !== 1 ? 's' : ''} available
                    {searchQuery && ` (filtered from ${newRawValue.length})`}
                  </span>
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                    style={{
                      all: 'unset',
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

/**
 * FileInput Component
 * 
 * Renders a file upload input with validation and display of uploaded file.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {string} data.acceptfieldtype - Array of accepted file types (e.g., ["image/*", "application/pdf"])
 * @param {string} data.framework - Framework context for tooltips
 * @param {Object} value - Current field value (object with url and id)
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @param {string} inputfiledId - Unique ID for the file input
 * @returns {JSX.Element} File input component
 */
export const FileInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-16", 
  inputfiledId,
}) => {
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : "mb-12";

  return (
    <div>
      {validationErrors && validationErrors[data?.title] && (
        <p className="text-[#f31b66] text-sm p-1">
          {validationErrors[data?.title]}
        </p>
      )}
      <label
        htmlFor={inputfiledId}
        className={"flex flex-col items-start gap-2" + cssStyle}
      >
        <div className={`mb-1 `}>
          {value?.url || data?.title}
          <span>
            {data?.required && <span style={{ color: "#f31b66" }}> *</span>}
          </span>
        </div>
        <div className="flex flex-col items-center gap-2 text-sm border-solid border-2 px-4 py-2 border-danger text-danger hover:text-white hover:bg-danger  rounded-lg max-w-max cursor-pointer transition ease-in duration-150">
          {"Upload File"}
        </div>
      </label>
      <input
        type="file"
        id={inputfiledId}
        accept={data?.acceptfieldtype?.join(",") || "*"}
        className="mb-16 hidden"
        onChange={(event) => {
          onChangeHandler(data?.title, event.target.files[0], data?.type);
        }}
      />
    </div>
  );
};

/**
 * CustomFrequencyInput Component
 * 
 * Renders a custom numeric input for frequency values.
 * 
 * @param {number} value - Current field value
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @returns {JSX.Element} Custom frequency input component
 */
export const CustomFrequencyInput = ({ 
  value, 
  validationErrors, 
  onChangeHandler,
}) => {
  return (
    <div className="flex flex-col gap-2 mb-16">
      <div className="w-full">
        <div className="field-title-wrapper">
          <span className="text-sm font-semibold text-neutral-700">
            Choose Custom Days
          </span>
          <span className="text-red-500 ml-1">*</span>
        </div>

        <Input
          type="number"
          isRequired
          min={0.01}
          step="0.01"
          placeholder="0"
          variant="faded"
          labelPlacement="outside"
          value={value && value}
          onChange={(event) => {
            onChangeHandler(+event.target.value);
          }}
          classNames={{
            input: "text-sm font-medium",
            inputWrapper: "bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200"
          }}
        />
      </div>
    </div>
  );
};

/**
 * MultiSelectInput Component
 * 
 * Renders a multi-select dropdown for multiple selections.
 * 
 * @param {Object} data - Field configuration data
 * @param {Array} valuesList - List of available options for the multi-select
 * @param {Array} value - Current field value (array of selected IDs)
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {Object} validationErrors - Validation error messages
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Multi-select input component
 */
export const MultiSelectInput = ({ 
  data, 
  valuesList, 
  value, 
  onChangeHandler, 
  validationErrors = {}, 
  cssStyle = "mb-8",
}) => {
  return (
    <div className={`flex flex-col gap-2 ${cssStyle}`}>
      <div className="w-full">
        <div className="field-title-wrapper">
          <span
            className={`text-sm font-semibold text-neutral-700 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "text-red-500"
                : ""
            }`}
          >
            {data?.title}
          </span>
          {data?.required && <span className="text-red-500 ml-1">*</span>}
        </div>

        <Select
          isRequired={data?.required}
          placeholder={data?.placeholder || " "}
          variant="faded"
          selectionMode="multiple"
          labelPlacement="outside"
          isInvalid={validationErrors && validationErrors[data?.title] && data?.required}
          errorMessage={validationErrors && validationErrors[data?.title]}
          selectedKeys={new Set(value)}
          onSelectionChange={(event) =>
            onChangeHandler(data?.title, Array.from(event), data?.type)
          }
          classNames={{
            trigger: `bg-white border border-neutral-200 hover:border-neutral-300 focus-within:border-emerald-500 transition-all duration-200 ${
              validationErrors && validationErrors[data?.title] && data?.required
                ? "border-red-300"
                : ""
            }`,
            value: "text-sm font-medium"
          }}
        >
          {valuesList?.map((item) => (
            <SelectItem key={item.id} value={item.id} className="py-2">
              {item.name}
            </SelectItem>
          ))}
        </Select>

        {validationErrors && validationErrors[data?.title] && data?.required && (
          <p className="text-danger text-xs mt-1">{validationErrors[data?.title]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * MetricSelectorInput Component
 * 
 * Renders a dynamic dropdown that shows all configured metrics.
 * Users can filter metrics by category and select from available metrics.
 * 
 * @param {Object} data - Field configuration data
 * @param {string} data.title - Field label/title
 * @param {boolean} data.required - Whether the field is required
 * @param {string} data.framework - Framework context for tooltips
 * @param {string|string[]} data.metricCategoryFilter - Optional category filter for metrics (single category or array of categories)
 * @param {string} value - Current field value (selected metric ID)
 * @param {Object} moreInfo - Additional information for tooltip
 * @param {Object} orgFrameworkDetails - Organization framework details
 * @param {Object} validationErrors - Validation error messages
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {string} cssStyle - CSS class for styling
 * @returns {JSX.Element} Metric selector component
 */
export const MetricSelectorInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-12",
}) => {
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Fetch metrics from the API
  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoading(true);
      try {
        // Get organization ID from session storage
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        const orgId = userData?.user?.LastOrgId || userData?.Organization?.id;

        if (!orgId) {
          console.error("Organization ID not found in session storage:", userData);
          return;
        }

        // Build filter URL for metrics
        let filterUrl = `filters[Organization][id][$eqi]=${orgId}`;

        if (data?.metricCategoryFilter) {
          // Handle both single category (string) and multiple categories (array)
          if (Array.isArray(data.metricCategoryFilter) && data.metricCategoryFilter.length > 0) {
            // Multiple categories - use $in operator
            const categories = data.metricCategoryFilter.join(',');
            filterUrl += `&filters[Category][$in]=${categories}`;
          } else if (typeof data.metricCategoryFilter === 'string' && data.metricCategoryFilter.trim() !== '') {
            // Single category - use $eqi operator
            filterUrl += `&filters[Category][$eqi]=${data.metricCategoryFilter}`;
          }
        }

        console.log("Fetching metrics with filter:", filterUrl);

        // Use the standard getDataWithFilterUrl function like other components
        const result = await getDataWithFilterUrl("metrics", filterUrl);

        console.log("Metrics API response:", result);

        if (result?.data) {
          setMetrics(result.data);
          console.log("Set metrics:", result.data.length, "metrics");
        } else {
          console.log("No data in API response");
        }
      } catch (error) {
        console.error("Error fetching metrics:", error);
        // Don't break the form if metrics can't be fetched
        setMetrics([]);
        // Show a user-friendly error message
        console.warn("Metric selector will show empty list due to API error. Form will still work.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [data?.metricCategoryFilter]);

  // Handle value changes separately to avoid infinite loops
  useEffect(() => {
    if (value && metrics.length > 0) {
      const found = metrics.find(metric => metric.id.toString() === value.toString());
      if (found) {
        setSelectedMetric(found);
        console.log("Found and set selected metric:", found.attributes?.Name);
      } else {
        console.log("Selected metric not found in fetched data. Value:", value, "Available IDs:", metrics.map(m => m.id));
        setSelectedMetric(null);
      }
    } else if (!value) {
      setSelectedMetric(null);
    }
  }, [value, metrics]);

  // Handle metric selection
  const handleMetricChange = (metricId) => {
    console.log("Metric selection changed:", metricId);
    const selected = metrics.find(metric => metric.id.toString() === metricId.toString());
    if (selected) {
      setSelectedMetric(selected);
      console.log("Selected metric:", selected.attributes?.Name);
      onChangeHandler(data?.title, metricId, data?.type);
      setIsModalOpen(false); // Close modal after selection
    } else {
      console.error("Selected metric not found in metrics array:", metricId, "Available:", metrics.map(m => m.id));
    }
  };

  // Get unique categories from metrics
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(metrics.map(metric => metric.attributes?.Category).filter(Boolean))];
    return ["All", ...uniqueCategories.sort()];
  }, [metrics]);

  // Filter metrics based on search and category
  const filteredMetrics = useMemo(() => {
    let filtered = metrics;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(metric => metric.attributes?.Category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(metric => 
        metric.attributes?.Name?.toLowerCase().includes(searchLower) ||
        metric.attributes?.Category?.toLowerCase().includes(searchLower) ||
        metric.attributes?.ClassName?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [metrics, selectedCategory, searchTerm]);

  // Adjust spacing based on title length
  const length = data?.title?.length || 0;
  cssStyle = length > 100 ? "mb-16" : cssStyle;

  return (
    <div className={`flex gap-2 ${cssStyle}`}>
      <div className="w-full">
        {/* Field Label */}
        {data?.title && (
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {data.title}
              {data?.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        )}

        {/* Metric Selector Button */}
        <Button 
          variant="faded" 
          className="w-full justify-between"
          isLoading={isLoading}
          isDisabled={isLoading}
          onClick={() => setIsModalOpen(true)}
        >
          <span>
            {selectedMetric ? selectedMetric.attributes?.Name || 'Select a metric' : 
              metrics.length === 0 && !isLoading ? 'No metrics available' : 'Select a metric'}
          </span>
          <FaChevronDown />
        </Button>

        {/* Metric Browser Modal */}
        <Modal 
          isOpen={isModalOpen} 
          onOpenChange={setIsModalOpen}
          size="3xl"
          scrollBehavior="inside"
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold">Select Metric</h3>
              <p className="text-sm text-gray-600">
                Choose a metric from the available options
              </p>
            </ModalHeader>

            <ModalBody>
              {/* Search Bar */}
              <div className="mb-4">
                <Input
                  placeholder="Search metrics by name, category, or class..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startContent={<span className="text-gray-400">🔍</span>}
                  className="w-full"
                />
              </div>

              {/* Category Pills */}
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Categories:</div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <StandardSecondaryButton
                      key={category}
                      size="sm"
                      variant={selectedCategory === category ? "solid" : "bordered"}
                      color={selectedCategory === category ? "success" : "default"}
                      onClick={() => setSelectedCategory(category)}
                      className="text-xs"
                    >
                      {category}
                    </StandardSecondaryButton>
                  ))}
                </div>
              </div>

              {/* Metrics List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredMetrics.length > 0 ? (
                  filteredMetrics.map((metric) => (
                    <div
                      key={metric.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleMetricChange(metric.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {metric.attributes?.Name || 'Unnamed Metric'}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {metric.attributes?.Category && (
                              <span className="mr-3">Category: {metric.attributes.Category}</span>
                            )}
                            {metric.attributes?.ClassName && (
                              <span>Class: {metric.attributes.ClassName}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 ml-2">
                          Click to select
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm.trim() || selectedCategory !== "All" 
                      ? "No metrics match your search criteria" 
                      : metrics.length === 0 && isLoading === false
                        ? "No metrics available - check API connection"
                        : "No metrics available"}
                  </div>
                )}
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Tooltip */}
        {moreInfo && (
          <FieldInputTooltip
            moreInfo={moreInfo}
            orgFrameworkDetails={orgFrameworkDetails}
            framework={data?.framework}
          />
        )}

        {/* Validation Error */}
        {validationErrors && validationErrors[data?.title] && (
          <div className="text-red-500 text-xs mt-1">
            {validationErrors[data?.title]}
          </div>
        )}
      </div>
    </div>
  );
};



/**
 * TreeInput Component
 * Renders a hierarchical tree structure input field
 * Allows users to build and edit tree data with nodes and relationships
 * 
 * @param {Object} data - Field configuration data
 * @param {Array} value - Current tree data value
 * @param {Function} onChangeHandler - Function to handle value changes
 * @param {Object} moreInfo - Additional information for tooltip
 * @returns {JSX.Element} Tree input interface
 */
export const TreeInput = ({ 
  data, 
  value, 
  moreInfo, 
  orgFrameworkDetails, 
  validationErrors, 
  onChangeHandler, 
  cssStyle = "mb-12",
}) => {
  // State for managing tree structure and UI interactions
  const [expandedNodes, setExpandedNodes] = useState(new Set()); // Track which nodes are expanded
  const [treeData, setTreeData] = useState(value || []); // Current tree data
  const [editingNode, setEditingNode] = useState(null); // Currently editing node ID
  const [showAddFieldModal, setShowAddFieldModal] = useState(false); // Show/hide add field modal
  const [selectedNodeForField, setSelectedNodeForField] = useState(null); // Node to add field to
  const [showJsonModal, setShowJsonModal] = useState(false); // Show/hide JSON export modal

  // Parse tree data from string or use array directly
  useEffect(() => {
    if (typeof value === 'string' && value) {
      try {
        setTreeData(JSON.parse(value));
      } catch (e) {
        setTreeData([]);
      }
    } else if (Array.isArray(value)) {
      setTreeData(value);
    } else {
      setTreeData([]);
    }
  }, [value]);

  /**
   * Toggle the expanded state of a node
   * @param {string} nodeId - ID of the node to toggle
   */
  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  /**
   * Update a specific field in a node
   * @param {string} nodeId - ID of the node to update
   * @param {string} fieldName - Name of the field to update
   * @param {any} fieldValue - New value for the field
   */
  const updateNodeData = (nodeId, fieldName, fieldValue) => {
    const updateNode = (nodes) => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, [fieldName]: fieldValue };
        }
        if (node.children) {
          return { ...node, children: updateNode(node.children) };
        }
        return node;
      });
    };
    const updatedData = updateNode(treeData);
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  /**
   * Render a single tree node with all its controls and children
   * @param {Object} node - Node data to render
   * @param {number} level - Current nesting level (0 for root)
   * @returns {JSX.Element} Rendered tree node
   */
  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isLeaf = !hasChildren;
    const isEditing = editingNode === node.id;

    return (
      <div key={node.id} className="w-full">
        <div 
          className={`flex items-center gap-2 p-2 border-l-2 border-emerald-200 hover:bg-emerald-50 transition-colors ${
            level > 0 ? 'ml-4' : ''
          }`}
          style={{ borderLeftColor: level === 0 ? '#10b981' : level === 1 ? '#34d399' : '#6ee7b7' }}
        >
          {/* Expand/Collapse Icon - Only show if node has children */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(node.id)}
              className="p-1 hover:bg-emerald-200 rounded transition-colors"
            >
              <svg 
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Node Content - Either editing form or display view */}
          <div className="flex-1">
            {isEditing ? (
              // Editing mode - show input fields
              <div className="space-y-2">
                <Input
                  size="sm"
                  value={node.title || ''}
                  onChange={(e) => updateNodeData(node.id, 'title', e.target.value)}
                  placeholder="Node title"
                />
                <Input
                  size="sm"
                  value={node.description || ''}
                  onChange={(e) => updateNodeData(node.id, 'description', e.target.value)}
                  placeholder="Node description (optional)"
                />
                <div className="flex gap-2">
                  <StandardPrimaryButton
                    size="sm"
                    onClick={() => setEditingNode(null)}
                    className="text-xs"
                  >
                    Save
                  </StandardPrimaryButton>
                  <StandardCancelButton
                    size="sm"
                    onClick={() => setEditingNode(null)}
                    className="text-xs"
                  >
                    Cancel
                  </StandardCancelButton>
                </div>
              </div>
            ) : (
              // Display mode - show node information
              <div>
                <div className="font-medium text-gray-900">{node.title || 'Untitled Node'}</div>
                {node.description && (
                  <div className="text-sm text-gray-600 mt-1">{node.description}</div>
                )}
                {/* Show embedded fields if they exist */}
                {node.fields && node.fields.length > 0 && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <div className="text-xs text-gray-600 mb-2 flex justify-between items-center">
                      <span>Embedded Fields:</span>
                      <StandardDeleteButton
                        size="sm"
                        onClick={() => {
                          // Remove all fields from this node
                          node.fields.forEach(field => {
                            removeFieldFromNode(node.id, field.id);
                          });
                        }}
                        className="text-xs px-1"
                      >
                        Remove All
                      </StandardDeleteButton>
                    </div>
                    <div className="space-y-2">
                      {node.fields.map((field, index) => (
                        <div key={index} className="flex items-center gap-2">
                          {renderEmbeddedField(field, node.id)}
                          <StandardDeleteButton
                            size="sm"
                            onClick={() => removeFieldFromNode(node.id, field.id)}
                            className="text-xs px-1"
                          >
                            ✕
                          </StandardDeleteButton>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons - Only show when not editing */}
          {!isEditing && (
            <div className="flex gap-1">
              <StandardSecondaryButton
                size="sm"
                onClick={() => setEditingNode(node.id)}
                className="text-xs px-2"
              >
                Edit
              </StandardSecondaryButton>
              <StandardAddButton
                size="sm"
                onClick={() => {
                  setSelectedNodeForField(node.id);
                  setShowAddFieldModal(true);
                }}
                className="text-xs px-2"
              >
                Add Field
              </StandardAddButton>
              <StandardAddButton
                size="sm"
                onClick={() => addNode(node.id)}
                className="text-xs px-2"
              >
                Add Child
              </StandardAddButton>
              <StandardDeleteButton
                size="sm"
                onClick={() => removeNode(node.id)}
                className="text-xs px-2"
              >
                Remove
              </StandardDeleteButton>
            </div>
          )}

          {/* Data Entry Form for Leaf Nodes - Only show for leaf nodes */}
          {isLeaf && !isEditing && (
            <div className="flex gap-2">
              <StandardSecondaryButton
                size="sm"
                onClick={() => updateNodeData(node.id, 'hasForm', !node.hasForm)}
                className="text-xs px-2"
              >
                {node.hasForm ? 'Disable Form' : 'Enable Form'}
              </StandardSecondaryButton>

              {/* Show form fields if form is enabled */}
              {node.hasForm && (
                <div className="flex gap-2">
                  {node.formFields?.map((field, index) => (
                    <div key={index} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-600">{field.label}</label>
                      <Input
                        size="sm"
                        placeholder={field.placeholder || field.label}
                        value={node.formData?.[field.name] || ''}
                        onChange={(e) => updateNodeData(node.id, 'formData', {
                          ...node.formData,
                          [field.name]: e.target.value
                        })}
                        className="w-32"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Render children recursively if node is expanded */}
        {hasChildren && isExpanded && (
          <div className="ml-4 border-l border-gray-200">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Add a new node to the tree
   * @param {string|null} parentId - ID of parent node (null for root level)
   */
  const addNode = (parentId = null) => {
    const newNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: 'New Node',
      description: '',
      children: [],
      fields: [],
      hasForm: false,
      formFields: [],
      formData: {}
    };

    const addNodeToTree = (nodes) => {
      return nodes.map(node => {
        if (node.id === parentId) {
          return { ...node, children: [...(node.children || []), newNode] };
        }
        if (node.children) {
          return { ...node, children: addNodeToTree(node.children) };
        }
        return node;
      });
    };

    const updatedData = parentId ? addNodeToTree(treeData) : [...treeData, newNode];
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  /**
   * Add a new field to a specific node
   * @param {string} nodeId - ID of the node to add field to
   * @param {string} fieldType - Type of field to add
   * @param {Object} fieldConfig - Configuration for the new field
   */
  const addFieldToNode = (nodeId, fieldType, fieldConfig) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: fieldType,
      name: fieldConfig.name || fieldType,
      title: fieldConfig.title || FieldList[fieldType]?.title || fieldType,
      required: fieldConfig.required || false,
      placeholder: fieldConfig.placeholder || '',
      ...fieldConfig
    };

    const addFieldToTree = (nodes) => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, fields: [...(node.fields || []), newField] };
        }
        if (node.children) {
          return { ...node, children: addFieldToTree(node.children) };
        }
        return node;
      });
    };

    const updatedData = addFieldToTree(treeData);
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  /**
   * Remove a field from a specific node
   * @param {string} nodeId - ID of the node to remove field from
   * @param {string} fieldId - ID of the field to remove
   */
  const removeFieldFromNode = (nodeId, fieldId) => {
    const removeFieldFromTree = (nodes) => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return { ...node, fields: node.fields?.filter(field => field.id !== fieldId) || [] };
        }
        if (node.children) {
          return { ...node, children: removeFieldFromTree(node.children) };
        }
        return node;
      });
    };

    const updatedData = removeFieldFromTree(treeData);
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  /**
   * Update the value of a specific field in a node
   * @param {string} nodeId - ID of the node containing the field
   * @param {string} fieldId - ID of the field to update
   * @param {any} value - New value for the field
   */
  const updateFieldValue = (nodeId, fieldId, value) => {
    const updateFieldInTree = (nodes) => {
      return nodes.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            fields: node.fields?.map(field => 
              field.id === fieldId ? { ...field, value } : field
            ) || []
          };
        }
        if (node.children) {
          return { ...node, children: updateFieldInTree(node.children) };
        }
        return node;
      });
    };

    const updatedData = updateFieldInTree(treeData);
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  /**
   * Render an embedded field based on its type
   * @param {Object} field - Field configuration
   * @param {string} nodeId - ID of the node containing the field
   * @returns {JSX.Element} Rendered field input
   */
  const renderEmbeddedField = (field, nodeId) => {
    const commonProps = {
      size: "sm",
      label: field.title,
      placeholder: field.placeholder,
      isRequired: field.required,
      value: field.value || '',
      onChange: (e) => updateFieldValue(nodeId, field.id, e.target.value),
      className: "w-full"
    };

    // Render different input types based on field.type
    switch (field.type) {
      case 'text':
        return <Input {...commonProps} type="text" />;
      case 'textarea':
        return <Textarea {...commonProps} />;
      case 'integer':
        return <Input {...commonProps} type="number" />;
      case 'date':
        return <DatePicker {...commonProps} />;
      case 'dropdown':
        return (
          <div className="relative w-full">
            <Button 
              variant="faded" 
              className="w-full justify-between"
              onPress={() => {
                // For now, keep the dropdown behavior in this context
                // as it's part of a complex tree structure
                const dropdown = document.querySelector(`[data-field-id="${field.id}"]`);
                if (dropdown) {
                  dropdown.click();
                }
              }}
            >
              <span>{field.value || field.placeholder}</span>
              <FaChevronDown />
            </Button>

            {/* Hidden dropdown for compatibility */}
            <div className="hidden">
              <Dropdown>
                <DropdownTrigger className="w-full" data-field-id={field.id}>
                  <Button variant="faded" className="w-full justify-between">
                    <span>{field.value || field.placeholder}</span>
                    <FaChevronDown />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Options"
                  onSelectionChange={(event) => {
                    const selected = Array.from(event)[0];
                    updateFieldValue(nodeId, field.id, selected);
                  }}
                >
                  {field.values?.map((option) => (
                    <DropdownItem key={option.id || option} value={option.name || option}>
                      {option.name || option}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
        );
      case 'boolean':
        return (
          <Switch
            isSelected={field.value === 'true'}
            onValueChange={(value) => updateFieldValue(nodeId, field.id, value.toString())}
          />
        );
      default:
        return <Input {...commonProps} />;
    }
  };

  /**
   * Remove a node and all its children from the tree
   * @param {string} nodeId - ID of the node to remove
   */
  const removeNode = (nodeId) => {
    const removeNodeFromTree = (nodes) => {
      return nodes.filter(node => {
        if (node.id === nodeId) {
          return false;
        }
        if (node.children) {
          node.children = removeNodeFromTree(node.children);
        }
        return true;
      });
    };

    const updatedData = removeNodeFromTree(treeData);
    setTreeData(updatedData);
    onChangeHandler(data?.title, JSON.stringify(updatedData), data?.type);
  };

  return (
    <div className={`${cssStyle}`}>
      {/* Header with controls */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            {data?.title || "Tree Structure"}
            {data?.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex gap-2">
                        <StandardSecondaryButton
              size="sm"
              onClick={() => setShowJsonModal(true)}
            >
              Export JSON
            </StandardSecondaryButton>
            <StandardPrimaryButton
              size="sm"
              onClick={() => addNode()}
            >
              Add Root Node
            </StandardPrimaryButton>
          </div>
        </div>

        {/* Tree Structure Display */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          {treeData.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No nodes added yet. Click "Add Root Node" to get started.
            </div>
          ) : (
            <div className="space-y-1">
              {treeData.map(node => renderTreeNode(node))}
            </div>
          )}
        </div>

        {/* Validation Error Display */}
        {validationErrors && validationErrors[data?.title] && (
          <p className="text-red-500 text-sm mt-2">{validationErrors[data?.title]}</p>
        )}
      </div>

      {/* Add Field Modal */}
      {showAddFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Field to Node</h3>
              <button
                onClick={() => setShowAddFieldModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Field Type
                </label>
                <div className="relative w-full">
                  <Button 
                    variant="faded" 
                    className="w-full justify-between"
                    onPress={() => {
                      // For now, keep the dropdown behavior in this context
                      // as it's part of a complex tree structure
                      const dropdown = document.querySelector('[data-field-type-selector]');
                      if (dropdown) {
                        dropdown.click();
                      }
                    }}
                  >
                    <span>Choose field type...</span>
                    <FaChevronDown />
                  </Button>

                  {/* Hidden dropdown for compatibility */}
                  <div className="hidden">
                    <Dropdown>
                      <DropdownTrigger className="w-full" data-field-type-selector>
                        <Button variant="faded" className="w-full justify-between">
                          <span>Choose field type...</span>
                          <FaChevronDown />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Field Types"
                        className="w-full"
                        onSelectionChange={(event) => {
                          const selectedType = Array.from(event)[0];
                          if (selectedType) {
                            const fieldConfig = {
                              name: `field_${selectedType}`,
                              title: FieldList[selectedType]?.title || selectedType,
                              required: false,
                              placeholder: `Enter ${FieldList[selectedType]?.title || selectedType}`
                            };

                            addFieldToNode(selectedNodeForField, selectedType, fieldConfig);
                            setShowAddFieldModal(false);
                          }
                        }}
                      >
                        {Object.entries(FieldList).map(([key, field]) => (
                          <DropdownItem key={key} className="flex items-center gap-2">
                            <span className="w-4 h-4">{field.icon}</span>
                            <span>{field.title}</span>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p>This will add a form field to the selected node. The field will be embedded within the tree structure and can be used for data collection.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Export Modal */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Export Tree Structure as JSON</h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p>Copy this JSON to use in other applications or for backup purposes. You can also paste this JSON back into the configuration to restore the tree structure.</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Tree Structure JSON:</label>
                  <StandardCopyButton
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(treeData, null, 2));
                    }}
                    className="text-xs"
                  >
                    Copy to Clipboard
                  </StandardCopyButton>
                </div>
                <Textarea
                  value={JSON.stringify(treeData, null, 2)}
                  readOnly
                  className="w-full h-64 font-mono text-xs"
                  variant="faded"
                />
              </div>
              <div className="text-xs text-gray-500">
                <p><strong>Note:</strong> This JSON includes all node data, embedded fields, and their values. You can use this to:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Backup your tree structure</li>
                  <li>Share with other users</li>
                  <li>Import into other systems</li>
                  <li>Use as a template for new trees</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * changeHandler Function
 * 
 * Handles value changes for different input types, including file uploads.
 * This is a utility function that can be used by any form component to handle
 * different types of input changes consistently.
 * 
 * @param {string} title - Field title/name
 * @param {any} value - New value for the field
 * @param {string} type - Type of input (e.g., "text", "file", "boolean")
 * @param {Object} formdata - Current form data state
 * @param {Function} setFormData - Function to update form data state
 * @returns {Promise<void>} Promise that resolves when the change is processed
 */
export const changeHandler = async (
  title,
  value,
  type,
  formdata,
  setFormData
) => {
  if (type === "file") {
    // Handle file uploads
    let fileData = new FormData();
    fileData.append("uploaded_file", value);
    try {
      const data = await uploadFile(fileData);
      // Update form data with file information (ID and URL)
      setFormData({
        ...formdata,
        [title]: { value: data[0].id, url: data[0].url },
      });
    } catch (error) {
      console.error("Upload error!", error);
    }
  } else if (type === "boolean") {
    // Handle boolean values with proper string conversion
    if (value === "") {
      setFormData({ ...formdata, [title]: "false" });
    } else {
      setFormData({ ...formdata, [title]: value });
    }
  } else {
    // Handle all other input types
    setFormData({ ...formdata, [title]: value });
  }
};
